'use client';

import React from 'react';
import { ButtonProps } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

export type ConfirmDialogCdsProps = {
  title?: string | React.ReactNode;
  description: string | React.ReactNode;
  classNameConfirm?: string;
  classNameCancel?: string;
  button?: React.ReactElement<ButtonProps>;
  open?: boolean;
  confirmLabel?: string | React.ReactNode;
  cancelLabel?: string | React.ReactNode;
  onConfirm: () => void;
  onCancel?: () => void;
};

/**
 * A ConfirmDialogCds component that displays a confirmation dialog with customizable title, description, and buttons.
 *
 * The dialog can be triggered by a button element or by setting the `open` prop to `true`.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title of the confirmation dialog.
 * @param {string} props.description - The description text of the confirmation dialog.
 * @param {string} props.classNameConfirm - The CSS class name for the confirm button.
 * @param {string} props.classNameCancel - The CSS class name for the cancel button.
 * @param {React.ReactNode} props.button - The button element that triggers the dialog.
 * @param {boolean} props.open - The open state of the dialog.
 * @param {string} props.confirmLabel - The label for the confirm button.
 * @param {string} props.cancelLabel - The label for the cancel button.
 * @param {Function} props.onConfirm - The callback function to be called when the confirm button is clicked.
 * @param {Function} props.onCancel - The callback function to be called when the cancel button is clicked.
 *
 * @returns {JSX.Element} The rendered ConfirmDialogCds component.
 */
export function ConfirmDialogCds({
  title,
  description,
  classNameConfirm,
  classNameCancel,
  button,
  open,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<ConfirmDialogCdsProps>) {
  return (
    <AlertDialog open={button ? undefined : open}>
      {button && <AlertDialogTrigger asChild>{button}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          {title && <AlertDialogTitle className="text-left">{title}</AlertDialogTitle>}
          <AlertDialogDescription className="text-left">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onCancel?.()} className={cn('mt-0', classNameCancel)}>
            {cancelLabel || 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm()} className={classNameConfirm}>
            {confirmLabel || 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
